/**
 * Reimpostazione della password (V03) dal link ricevuto via email.
 *
 * Il codice si verifica all'apertura: un link scaduto o gia usato mostra
 * subito l'errore con il rimando a V02, invece che dopo aver scritto la
 * password. Confermata la nuova password, tutte le sessioni dell'account
 * sono chiuse e si torna all'accesso.
 */

import { type FormEvent, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { nuovaPasswordSchema } from '@calcio/shared';
import { chiama } from '../api/client.js';
import './accesso.css';

type Fase = 'verifica' | 'modulo' | 'non-valido';

export function PaginaReimposta() {
  const { codice = '' } = useParams();
  const [fase, setFase] = useState<Fase>('verifica');
  const [password, setPassword] = useState('');
  const [conferma, setConferma] = useState('');
  const [invio, setInvio] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    chiama(`/reimposta/${codice}`)
      .then(() => setFase('modulo'))
      .catch(() => setFase('non-valido'));
  }, [codice]);

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    const esito = nuovaPasswordSchema.safeParse(password);
    if (!esito.success) return setErrore(esito.error.issues[0]!.message);
    if (password !== conferma) return setErrore('Le due password non coincidono.');
    setInvio(true);
    setErrore(null);
    try {
      await chiama(`/reimposta/${codice}`, { metodo: 'POST', corpo: { password } });
      // Ricarica completa: le sessioni aperte, compresa un'eventuale in questo
      // browser, sono state chiuse dal server.
      window.location.replace('/accesso?password=aggiornata');
    } catch {
      setFase('non-valido');
      setInvio(false);
    }
  };

  return (
    <div className="accesso">
      <form className="accesso-scheda" onSubmit={(e) => void invia(e)}>
        <h1>Nuova password</h1>

        {fase === 'verifica' && <p className="accesso-nota">Controllo il link…</p>}

        {fase === 'non-valido' && (
          <>
            <p>Il link non è più valido: è scaduto o è già stato usato.</p>
            <Link to="/password-dimenticata" className="accesso-link">
              Chiedi un nuovo link
            </Link>
          </>
        )}

        {fase === 'modulo' && (
          <>
            {errore && (
              <div className="banda" role="alert">
                {errore}
              </div>
            )}

            <label className="campo">
              Nuova password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                minLength={10}
                required
              />
            </label>

            <label className="campo">
              Ripeti la password
              <input
                type="password"
                value={conferma}
                onChange={(e) => setConferma(e.target.value)}
                autoComplete="new-password"
                required
              />
            </label>

            <p className="accesso-nota">
              Almeno 10 caratteri. Gli altri dispositivi dovranno accedere di nuovo.
            </p>

            <button type="submit" disabled={invio}>
              {invio ? 'Salvataggio…' : 'Salva la password'}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
